import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posBackendApi } from '@/lib/api-client';

export type DiscountType = 'amount_off' | 'percent_off' | 'free_product';
export type CouponScope = 'order' | 'products' | 'exclude_categories';
export type CouponAudience = 'all' | 'segment';
export type LocationScope = 'all' | 'specific';
export type UsageType = 'single_use' | 'multi_use';

export interface Coupon {
  id: string;
  tenantId: string;
  code: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  discountType: DiscountType;
  discountValue: string;
  freeProductId: string | null;
  scope: CouponScope;
  minSpend: string;
  validFrom: string | null;
  validTo: string | null;
  audience: CouponAudience;
  segmentId: string | null;
  locationScope: LocationScope;
  usageType: UsageType;
  firstNCap: number | null;
  maxUsesPerCustomer: number | null;
  globalUseCount: number;
  showOnRegister: boolean;
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCouponInput {
  code: string;
  name: string;
  description?: string;
  imageUrl?: string;
  discountType: DiscountType;
  discountValue: number;
  freeProductId?: string;
  scope?: CouponScope;
  minSpend?: number;
  validFrom?: string;
  validTo?: string;
  audience?: CouponAudience;
  segmentId?: string;
  locationScope?: LocationScope;
  usageType?: UsageType;
  firstNCap?: number;
  maxUsesPerCustomer?: number;
  showOnRegister?: boolean;
}

const KEY = ['customer-engagement', 'coupons'] as const;

export function useCoupons(active?: boolean) {
  return useQuery({
    queryKey: [...KEY, { active }],
    queryFn: () =>
      posBackendApi.get<{ data: Coupon[]; meta: { count: number } }>('/customer-engagement/coupons', {
        active: active === undefined ? undefined : String(active),
      }),
    staleTime: 30_000,
  });
}

export function useCreateCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCouponInput) =>
      posBackendApi.post<{ data: Coupon }>('/customer-engagement/coupons', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useDeactivateCoupon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      posBackendApi.delete<{ data: { id: string; isActive: false } }>(`/customer-engagement/coupons/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
